package one.healthium.uscreen.ui.shell

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.AnimationType
import one.healthium.uscreen.domain.models.GradientBackground
import one.healthium.uscreen.domain.models.GradientDirection
import one.healthium.uscreen.domain.models.SolidBackground
import one.healthium.uscreen.domain.models.ThemeBackground
import one.healthium.uscreen.domain.models.UScreen
import one.healthium.uscreen.domain.models.UScreenBackground
import one.healthium.uscreen.domain.models.UScreenCta
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.UScreenCallbacks
import one.healthium.uscreen.ui.animation.BLOCK_APPEAR_DURATION
import one.healthium.uscreen.ui.animation.DEFAULT_BG_IN_ANIM
import one.healthium.uscreen.ui.animation.DEFAULT_BG_OUT_ANIM
import one.healthium.uscreen.ui.animation.LocalActiveScreenId
import one.healthium.uscreen.ui.animation.LocalNavigatingBack
import one.healthium.uscreen.ui.animation.LocalScreenOwnId
import one.healthium.uscreen.ui.animation.TRANSITION_EASING
import one.healthium.uscreen.ui.blocks.UScreenBlockColumn
import one.healthium.uscreen.ui.components.UScreenCtaBarSimple
import one.healthium.uscreen.ui.util.parseHexColor

@Composable
fun UScreenShell(
    modifier: Modifier = Modifier,
    screen: UScreen,
    cta: UScreenCta?,
    reserveCtaSpace: Boolean = cta != null,
    ctaEnabled: Boolean,
    onCtaClick: () -> Unit,
    answers: Map<String, UScreenAnswer>,
    isMale: Boolean?,
    placeholders: Map<String, String>,
    callbacks: UScreenCallbacks,
) {
    val density = LocalDensity.current
    val navBarHeight = with(density) { WindowInsets.navigationBars.getBottom(density).toDp() }
    // Freeze on first composition — layout must not change when screen becomes exiting
    val ctaReservedHeight = remember { if (reserveCtaSpace) 60.dp + 32.dp + navBarHeight else 0.dp }

    CompositionLocalProvider(LocalScreenOwnId provides screen.id) {
        Box(
            modifier = modifier
                .fillMaxSize()
                .testTag("uscreen_shell"),
        ) {
            // Layer 1: Background (animated separately)
            UScreenBackgroundLayer(background = screen.background, screenId = screen.id)

            // Layer 2: Block content (constant bottom padding for CTA area)
            UScreenBlockColumn(
                modifier = Modifier.fillMaxSize(),
                screen = screen,
                answers = answers,
                isMale = isMale,
                placeholders = placeholders,
                hasProgressBar = screen.progressBar,
                callbacks = callbacks,
                ctaHeightOverride = ctaReservedHeight,
                screenId = screen.id,
            )

            // Layer 3: CTA overlay
            if (cta != null) {
                UScreenCtaBarSimple(
                    cta = cta,
                    screenId = screen.id,
                    enabled = ctaEnabled,
                    onClick = onCtaClick,
                    modifier = Modifier.align(Alignment.BottomCenter),
                )
            }
        }
    }
}

@Composable
private fun UScreenBackgroundLayer(background: UScreenBackground?, screenId: String = "") {
    // Always render base opaque background (covers exiting screen behind)
    val baseBg = AppTheme.colors.windowBackground
    val baseTransitionModifier = baseBackgroundTransition(screenId)
    Box(modifier = Modifier.fillMaxSize().then(baseTransitionModifier).background(baseBg))

    if (background == null || background is ThemeBackground) return

    val transition = when (background) {
        is SolidBackground -> background.transition
        is GradientBackground -> background.transition
        is ThemeBackground -> null
    }

    val activeScreenId = LocalActiveScreenId.current
    val isBack = LocalNavigatingBack.current
    val isExiting = screenId.isNotEmpty() && activeScreenId.isNotEmpty() && screenId != activeScreenId
    val animType = when {
        isExiting && !isBack -> transition?.outAnim ?: DEFAULT_BG_OUT_ANIM  // push exit
        isExiting && isBack -> transition?.inAnim ?: DEFAULT_BG_IN_ANIM     // pop exit (reverse enter)
        !isExiting && !isBack -> transition?.inAnim ?: DEFAULT_BG_IN_ANIM   // push enter
        else -> transition?.outAnim ?: DEFAULT_BG_OUT_ANIM                  // pop enter (reverse exit)
    }
    if (animType == AnimationType.None && !isExiting) {
        // No animation needed — render immediately
    } else if (animType == AnimationType.None) {
        return // exiting with None — just disappear
    }

    val progress = remember(activeScreenId) { Animatable(if (isExiting) 1f else 0f) }
    LaunchedEffect(activeScreenId) {
        if (isExiting) {
            progress.animateTo(0f, tween(BLOCK_APPEAR_DURATION.toInt(), easing = TRANSITION_EASING))
        } else if (animType != AnimationType.None) {
            val blockDelay = (transition?.delay ?: 0f)
            val totalDelay = (blockDelay * 1000).toLong()
            delay(totalDelay)
            progress.animateTo(1f, tween(BLOCK_APPEAR_DURATION.toInt(), easing = TRANSITION_EASING))
        }
    }

    val p = if (animType == AnimationType.None) 1f else progress.value
    val transitionModifier = when (animType) {
        AnimationType.SlideOut -> Modifier.graphicsLayer {
            val remaining = 1f - p
            alpha = p
            translationX = when {
                isExiting && isBack -> remaining * size.width
                isExiting -> -remaining * size.width
                isBack -> -remaining * size.width
                else -> remaining * size.width
            }
        }

        AnimationType.Fade -> Modifier.graphicsLayer { alpha = p }
        AnimationType.None -> Modifier
    }

    val bgModifier = when (background) {
        is SolidBackground -> {
            val color = parseHexColor(background.color) ?: return
            Modifier.background(color)
        }

        is GradientBackground -> {
            val colors = background.colors.mapNotNull { parseHexColor(it) }
            if (colors.isEmpty()) return
            val brush = when (background.direction) {
                GradientDirection.TopToBottom -> Brush.verticalGradient(colors)
                GradientDirection.LeftToRight -> Brush.horizontalGradient(colors)
            }
            Modifier.background(brush)
        }

        is ThemeBackground -> return
    }

    Box(modifier = Modifier.fillMaxSize().then(transitionModifier).then(bgModifier))
}

/** Slides the opaque base background in sync with block transitions. */
@Composable
private fun baseBackgroundTransition(screenId: String): Modifier {
    val activeScreenId = LocalActiveScreenId.current
    val isBack = LocalNavigatingBack.current
    val isExiting = screenId.isNotEmpty() && activeScreenId.isNotEmpty() && screenId != activeScreenId

    val progress = remember(activeScreenId) { Animatable(if (isExiting) 1f else 0f) }
    LaunchedEffect(activeScreenId) {
        val target = if (isExiting) 0f else 1f
        progress.animateTo(target, tween(BLOCK_APPEAR_DURATION.toInt(), easing = TRANSITION_EASING))
    }

    val remaining = 1f - progress.value
    return Modifier.graphicsLayer {
        translationX = when {
            isExiting && isBack -> remaining * size.width
            isExiting -> -remaining * size.width
            isBack -> -remaining * size.width
            else -> remaining * size.width
        }
    }
}

